// CustomClipboardModule.ts
import Clipboard from 'quill/modules/clipboard'
import type { Quill } from 'react-quill';

type PropOptions = {
  uploadImage: (requestBody: Array<{file: string; type: string; fileName: string}>) => Promise<{error?: boolean; data?: Array<{key: string; name: string}>}>;
}

class CustomClipboardModule extends Clipboard {
  quill: Quill;
  options: PropOptions;

  constructor(quill: Quill, options: PropOptions) {
    super(quill, options);
    this.quill = quill;
    this.options = options;
  }

  async onPaste(e: ClipboardEvent) {
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    // Check if there are images in the clipboard
    const items = Array.from(clipboardData.items);
    const imageItems = items.filter(item => item.type.startsWith('image/'));

    if (imageItems.length > 0) {
      e.preventDefault(); // Prevent default paste behavior
      
      // Process each image
      for (const item of imageItems) {
        const file = item.getAsFile();
        if (file) {
          const fallback = !(await this.handleImagePaste(file));
          if (fallback) {
            super.onPaste(e);
          }
        }
      }
    } else {
      // For non-image content, use default paste behavior
      super.onPaste(e);
    }
  }

  async handleImagePaste(file: File) {
    const maxBase64Size = 20 * 1024; // 20KB threshold
    const fileSize = file.size;

    try {
      if (fileSize > maxBase64Size) {
        // Upload large images
        return this.uploadImage(file);
      } else {
        // Use existing imageCompress for smaller images
        return false;
      }
    } catch (error) {
      console.error('Image processing failed:', error);
      return false;
    }
  }

  async uploadImage(file: File) {
    try {
      // Convert file to base64
      const base64File = await this.convertBase64(file);
      if (typeof base64File === 'string') {
        const fileBase64 = base64File.split(',')[1];
        
        // Create request body matching the API format
        const requestBody = [{
          file: fileBase64,
          type: file.type,
          fileName: file.name,
        }];
        
        // Use your existing API endpoint
        const uploadResult = await this.options.uploadImage(requestBody);
        
        if (!uploadResult.error && uploadResult.data) {
          // Remove loading text and insert image URL
          const range = this.quill.getSelection();
          if (range) {
            // Use the first uploaded file's key as URL
            const imageUrl = uploadResult.data[0]?.key;
            if (imageUrl) {
              // the first part of key is the SUB_FOLDER name which can be trim
              const imageUrlWithoutSubFolder = imageUrl.split('/').slice(1).join('/');
              this.quill.insertEmbed(range.index, 'image', `${process.env.REACT_APP_WEB_BI_API_URL}/file/${imageUrlWithoutSubFolder}?redirect=true`);
              this.quill.setSelection({ index: range.index + 1, length: 0 });
            }
          }
        } else {
          throw new Error('Upload failed');
        }
        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  }

  convertBase64 = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        resolve(fileReader.result as string);
      };
      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };
}

export default CustomClipboardModule;